<template>
  <div class="group-detail">
    <h1 class="common__main-title">Application</h1>
    <div class="common__sub-title">
      Details
      <span class="common__sub-title-tag">Update</span>
    </div>
    <div class="common__tutor-content"
      v-if="application && (application.targetTutor || application.lesson.teacher.tutor)">
      <TutorInfo v-if="application.targetTutor" :tutor="application.targetTutor" />
      <TutorInfo v-else :tutor="application.lesson.teacher.tutor" />
    </div>
    <div class="content-wrapper">
      <div class="main-content">
        <div class="form-group">
          <label>Application Type</label>
          <div class="col-sm-10 form-confirm-value">
            {{ application && application.type | type }}
          </div>

          <label>Status</label>
          <div class="col-sm-10 form-confirm-value">
            {{ application && application.status | status }}
          </div>
        </div>

        <hr />

        <component
          v-if="application && application.applicant"
          :is="application && application.type"
          :initialValues="application"
          :isConfirmed="true"
          :status="application && application.status"
        />
      </div>

      <div v-if="isWithdrawable || isApprovable || isCancelApprove" class="form-group">
        <label v-show="isApprovable">Approval / Reject Comment</label>
        <label v-show="isWithdrawable || isCancelApprove">Cancel Comment</label>
        <ValidationProvider rules="required" ref="comment" v-slot="{ errors }">
          <Textarea class="form-control" v-model="comment" />
          <div class="text-danger" v-for="(error, i) of errors" :key="i">{{ error }}</div>
        </ValidationProvider>
      </div>

      <div class="d-flex justify-content-between">
        <b-button pill variant="secondary" :to="{ name: 'Applications' }">Back</b-button>
        <template v-if="isWithdrawable">
          <b-button pill variant="danger" @click="updateStatus('withdraw')">Withdraw</b-button>
        </template>
        <template v-if="isCancelApprove">
          <b-button pill variant="danger" @click="updateStatus('cancelApprove')">
            Cancel approve
          </b-button>
        </template>
        <template v-if="isApprovable">
          <b-button pill variant="danger" @click="updateStatus('reject')">Reject</b-button>
          <b-button pill variant="primary" @click="updateStatus('approve')">Approve</b-button>
        </template>
      </div>
    </div>

    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import _ from 'lodash'
import { ValidationProvider } from 'vee-validate'
import Vacation from '@/components/parts/application/types/Vacation.vue'
import Booth from '@/components/parts/application/types/Booth.vue'
import WorkOvertime from '@/components/parts/application/types/WorkOvertime.vue'
import Spir from '@/components/parts/application/types/Spir.vue'
import Inquiry from '@/components/parts/application/types/EvaluationInquiry.vue'
import TutorInfo from '@/components/parts/TutorInfo.vue'
import Textarea from '@/components/parts/inputItem/Textarea.vue'
import { VacationApplication } from '@/store/applications/vacations/types'
import { BoothApplication } from '@/store/applications/booths/types'
import { WorkOvertimeApplication } from '@/store/applications/workOvertime/types'
import { SpirApplication } from '@/store/applications/spir/types'
import { RootState } from '@/store/types'
import { Tutor } from '@/store/tutors/types'
import {
  ApplicationStatus,
  applicationStatusCode,
  WithdrawApplication,
  ApplicationType,
  applicationTypeCode,
} from '@/store/applications/types'
import Loading from '@/components/parts/modal/Loading.vue'

export default Vue.extend({
  components: {
    TutorInfo,
    Textarea,
    Vacation,
    Booth,
    WorkOvertime,
    Spir,
    Inquiry,
    ValidationProvider,
    Loading,
  },

  filters: {
    status: (s: applicationStatusCode): string => ApplicationStatus[s],
    type: (t: applicationTypeCode): string => ApplicationType[t],
  },

  computed: {
    application(): VacationApplication|BoothApplication|WorkOvertimeApplication|SpirApplication {
      return this.$store.getters.findApplicationById(this.$route.params.applicationId)
    },
    /**
     * 取り下げ可能判定
     * @returns {boolean} true: 可能、false: 不可能
     */
    isWithdrawable(): boolean {
      if (this.application?.status !== 'unapproved') {
        // 申請中以外の場合は不可
        return false
      }

      const profile = (this.$store.state as RootState).auth.profile as Tutor
      if (!profile) {
        // データ読み込み中はfalse
        return false
      }

      if (this.application?.applicant?.id === profile.id) {
        // 申請者のみキャンセルできる
        return true
      }

      return false
    },
    /**
     * 承認/却下可能判定
     * @returns {boolean} true:可能、false:不可能
     */
    isApprovable(): boolean {
      if (this.application?.status !== 'unapproved') {
        // 申請中以外の場合は不可
        return false
      }

      const profile = (this.$store.state as RootState).auth.profile as Tutor
      if (
        profile.authority === 'senior'
        && this.application?.applicant?.id !== profile.id
      ) {
        // シニアかつ申請者でない場合のみ承認/却下できる
        return true
      }

      return false
    },
    /**
     * 承認取り消し可能判定
     * @returns {boolean} true:可能、false:不可能
     */
    isCancelApprove(): boolean {
      if (this.application?.status !== 'approved') {
        // 承認中以外の場合は不可
        return false
      }

      const profile = (this.$store.state as RootState).auth.profile as Tutor
      if (
        profile.authority === 'senior'
        && this.application?.applicant?.id !== profile.id
      ) {
        // シニアかつ申請者でない場合のみ承認取り消しできる
        return true
      }

      return false
    },
  },
  data(): {
    comment: string
    isLoading: boolean
    } {
    return {
      comment: '',
      isLoading: false,
    };
  },

  created() {
    const { applicationId } = this.$route.params
    this.isLoading = true
    this.$store.dispatch('getApplicationById', { applicationId })
      .finally(() => { this.isLoading = false })
  },
  methods: {
    async updateStatus(action: 'withdraw'|'approve'|'reject'|'cancelApprove'): Promise<void> {
      if (this.isLoading) {
        return
      }

      const validationResults = await Promise.all(['comment'].map((key) => (this.$refs[key] as any).validate()))
      const errorLength = _.filter(validationResults, { valid: false }).length
      if (errorLength > 0) {
        return
      }

      if (!await this.$bvModal.msgBoxConfirm(`Are you sure you want to ${action} this application?`)) {
        return
      }

      const contents: WithdrawApplication = {
        applicationId: this.$route.params.applicationId,
        comment: this.comment,
      }
      try {
        this.isLoading = true
        switch (action) {
          case 'approve':
            await this.$store.dispatch('approveApplication', contents)
            break
          case 'reject':
            await this.$store.dispatch('rejectApplication', contents)
            break
          case 'withdraw':
            await this.$store.dispatch('withdrawApplication', contents)
            break;
          case 'cancelApprove':
            await this.$store.dispatch('cancelApproveApplication', contents)
            break;
          default:
            throw new Error(`申請のステータス更新時に把握していないステータスを指定されました。${action}`)
        }

        this.isLoading = false
        await this.$bvModal.msgBoxOk(`Completed ${action}`)
        this.$router.go(0)
      } catch (error: any) {
        this.isLoading = false
        if (_.has(error, 'response')) {
          this.$bvModal.msgBoxOk(`Failed: ${error.response.data.message}`)
        } else {
          this.$bvModal.msgBoxOk(`Failed: ${error.message}`)
        }
      }
    },
  },
})
</script>

<style lang="scss">
.content-wrapper {
  background: #D8D8D8;
  padding: 20px 50px;
}

.main-content {
  background: #fff;
  padding-top: 20px;
  border-radius: 20px;
  margin-bottom:20px;
  min-height: 500px;
}

.form-group {
  label {
    font-weight: bold;
    text-align: left;
    margin:10px 0;
  }

  .form-confirm-value {
    height:20px;
    margin-bottom: 20px;
    margin-left: 10px;
    font-size: 16px;
  }
}
</style>
